import { IApiManagement } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import * as React from "react";
import KuikaEntitySwitcher from "../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { API_NAME_MESSAGE, API_NAME_PATTERN } from "./api-name-modal";

/**
 * The API editor's header band: the API's name (which is also the rename control and the
 * API switcher) and the back control.
 *
 * There is no CANCEL and no UPDATE. Both are gone because the rename now SAVES when it is
 * submitted: the old UPDATE button wrote the staged name and closed the API in one click,
 * and CANCEL ran the same close path the back arrow does. Delete lives on the landing
 * table, one click away through that arrow.
 *
 * Presentation only — every handler belongs to the editor. The title must render INSIDE
 * the editor's antd `Form`: the field is `name`, seeded from the open API.
 */

export interface IApiEditorHeaderProps {
  apiName: string;
  isNameOnEdit: boolean;
  onStartRename: () => void;
  /** Called with the validated name; the host writes it. */
  onSubmitName: (name: string) => void;
  onAbortRename: () => void;
  /** The uniqueness rule, owned by the host so create and rename share one. */
  nameValidator: (rule: unknown, value: string) => Promise<void>;
  /** The reserved-key rule; same reason. */
  reservedValidator: (rule: unknown, value: string) => Promise<void>;
  /** Runs the field's rules and resolves the accepted name, or `undefined` if invalid. */
  validateName: () => Promise<string | undefined>;

  apis: IApiManagement[];
  currentApi?: IApiManagement;
  onSwitchApi: (api: IApiManagement) => void;
  onCreateNew: () => void;

  /** Leaving the editor. The bar's back control draws the only control for it. */
  onBack: () => void;
  /** The API list's own row actions, for the switcher's "…". */
  rowActions: KuikaListTableRowAction<IApiManagement>[];
}

export class ApiEditorHeader extends React.PureComponent<IApiEditorHeaderProps> {
  /**
   * Validation is awaited rather than read off the last result. Both rules are async and
   * the field is freshly mounted each time the pencil is clicked, so `getFieldError` is
   * empty on the first Enter and would let a duplicate through — which is what the
   * pre-refactor `handleSubmitNameChange` did. An invalid name still fails silently; the
   * field's own message is the feedback.
   */
  handleSubmitName = async (): Promise<void> => {
    const name: string | undefined = await this.props.validateName();
    if (name === undefined) return;
    this.props.onSubmitName(name);
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter") return;
    // Stops the antd Form from submitting and keeps the designer's global key handlers
    // from seeing this Enter.
    e.stopPropagation();
    e.preventDefault();
    void this.handleSubmitName();
  };

  renderTitle = (): React.ReactNode => (
    <KuikaEditorTitle
      className="kuika-api-builder__title"
      entityLabel="API"
      isRenaming={this.props.isNameOnEdit}
      onStartRename={this.props.onStartRename}
      onConfirmRename={this.handleSubmitName}
      onAbortRename={this.props.onAbortRename}
      editTestId="editApiNameButton"
      confirmTestId="submitApiNameButton"
      abortTestId="cancelApiNameButton"
      identity={
        <KuikaEntitySwitcher<IApiManagement>
          trigger="chip"
          triggerIcon={<KuikaSvg name="api-service" width={16} height={16} />}
          triggerLabel={this.props.apiName}
          triggerLabelTestId="apiEditorTitle"
          items={this.props.apis}
          current={this.props.currentApi}
          onSelect={this.props.onSwitchApi}
          onCreateNew={this.props.onCreateNew}
          entityLabel="API"
          entityLabelPlural="APIs"
          iconName="api-service"
          triggerTestId="apiSwitcherTrigger"
          itemTestId={(api: IApiManagement) => `${api.name.replace(/\s/g, "")}SwitcherItem`}
          rowActions={this.props.rowActions}
          onManageAll={this.props.onBack}
        />
      }
      renameField={
        <Form.Item
          name="name"
          className="kuika-api-builder__title-field"
          rules={[
            { required: true, message: "Please enter a name" },
            { pattern: API_NAME_PATTERN, message: API_NAME_MESSAGE },
            { validator: this.props.nameValidator },
            { validator: this.props.reservedValidator }
          ]}
        >
          <KuikaInput data-testid="renameApiNameInput" onKeyDown={this.handleKeyDown} placeholder="Name" autoFocus />
        </Form.Item>
      }
    />
  );

  render() {
    return (
      <KuikaPageHeader
        variant="bar"
        title={this.props.apiName || "API Builder"}
        onBack={this.props.onBack}
        backLabel="Back to API Builder"
        // Back is the only way out of the editor now, so it carries the id the suite used
        // to resolve on the CANCEL button it replaced.
        backTestId="cancelButton"
        renderTitle={this.renderTitle}
      />
    );
  }
}
